import { Stack, Box, Heading, Text } from 'braid-design-system';
import * as styles from './Profile.css';

import { observer } from 'mobx-react';

import RoleRequirementsView from 'src/components/list/JobApplicationCard/RoleRequirementsView';

import { CertsyMoreInfoWithVerifications } from '../CertsyMoreInfoWithVerifications/CertsyMoreInfoWithVerifications';
import { transformProfileToComponentProps } from './Profile/service/transformProfile';
import ProfileComponent from './Profile/components/Profile';
import type { IJobApplication } from 'src/stores/JobApplicationStore/JobApplication';
import { useStores } from 'src/utils/hooks/useStores';
import { ProfileIcon } from 'src/shared/icons/ProfileIcon';
import translations from './Profile/.vocab';
import { useTranslations } from '@vocab/react';
import AttachmentList from 'src/components/list/JobApplicationCard/ExtendedInfoList/AttachmentList';
import { getAdvertiserIds } from 'src/utils/advertiserIds';
import { logError } from 'src/utils/logError';
import { errorCodes } from 'src/constants/errorTypes';
import Loader from 'src/components/common/Loader';
import { useSalaryQuestionnaireQuery } from './DetailsContentView.hirer.generated';
import { convertToIsVerifiedId } from 'src/utils/eventMappers';

export interface Props {
  jobApplication: IJobApplication;
  questionnaireId?: string;
}

const Profile: React.FC<Props> = ({ jobApplication, questionnaireId }) => {
  const {
    roleRequirements,
    verifications,
    attachments,
    candidateId,
    jobApplicationId,
    profile,
    certifiedAnswersTotalCount,
  } = jobApplication;
  const { isMobile } = useStores();
  const { t } = useTranslations(translations);
  const transformedProfile = profile
    ? transformProfileToComponentProps(profile)
    : null;
  const showRoleReqsContent = roleRequirements.length > 0;
  const showDocuments = isMobile && attachments.length > 0;
  const showProfileTabContent =
    showRoleReqsContent || transformedProfile || showDocuments;

  const { loading, data, error } = useSalaryQuestionnaireQuery({
    variables: {
      questionnaireId: questionnaireId ?? '',
      adcentreAdvertiserId: getAdvertiserIds().advertiserId ?? '',
    },
    onError(err) {
      logError(
        {
          errorCode: errorCodes.GET_EXPECTED_SALARY,
          description: 'Error attempting: fetch expected salary data',
        },
        {
          err,
          questionnaireId,
          adcentreAdvertiserId: getAdvertiserIds().advertiserId,
        },
      );
    },
    skip:
      !transformedProfile ||
      !questionnaireId ||
      !getAdvertiserIds().advertiserId,
  });

  if (loading) {
    return <Loader />;
  }

  const salaryQuestions = Boolean(error)
    ? []
    : data?.questionnaire?.salaryQuestions || [];
  const shouldDisplayProfileSalarySection = salaryQuestions.length === 0;

  if (showProfileTabContent) {
    return (
      <Box marginBottom="xlarge" className={styles.width}>
        <Stack space="medium" dividers>
          {showDocuments && (
            <Stack space="large">
              <Heading level="3">{t('Application documents')}</Heading>
              <AttachmentList
                attachments={attachments}
                candidateId={candidateId}
                isVerifiedId={convertToIsVerifiedId(profile)}
              />
            </Stack>
          )}
          {showRoleReqsContent && (
            <Stack space="large">
              <Heading level="3">{t('Application questions')}</Heading>
              <RoleRequirementsView
                jobApplicationId={jobApplicationId}
                roleRequirements={roleRequirements}
                certifiedAnswersTotalCount={certifiedAnswersTotalCount}
                isMobile={isMobile}
              />
              {verifications.length > 0 && (
                <CertsyMoreInfoWithVerifications
                  verifications={verifications}
                />
              )}
            </Stack>
          )}
          {transformedProfile && (
            <ProfileComponent
              profile={transformedProfile}
              jobApplicationId={jobApplicationId}
              shouldDisplayProfileSalarySection={
                shouldDisplayProfileSalarySection
              }
            />
          )}
        </Stack>
      </Box>
    );
  }
  return (
    <Stack space="medium">
      <Box display="flex" justifyContent="center">
        <ProfileIcon />
      </Box>
      <Box display="flex" justifyContent="center">
        <Text weight="strong">
          {t('No Profile for ...', {
            fullName: jobApplication.fullName,
          })}
        </Text>
      </Box>
    </Stack>
  );
};

export default observer(Profile);
